import { isPlainObject } from "es-toolkit";

import { type DesignerNodeData, type Mutable } from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode } from "../types/index.ts";
import { profiler } from "./buildDesignerNodeProfiling.ts";
import type { MonolingualizeConfig } from "./contextualSelectors/index.ts";
import type { GetNodeTitle } from "./getNodeTitle.ts";
import type { GetNodeProfileById } from "./nodePublicApi.ts";

interface Options {
  getNodeTitle: GetNodeTitle;
  monolingualizeConfig: MonolingualizeConfig;
  getNodeProfileById: GetNodeProfileById;
}

export type BuildDesignerNode = (node: DesignerNodeData) => DesignerNode;

const designerNodeCache = new WeakMap<DesignerNodeData, WeakMap<MonolingualizeConfig, DesignerNode>>();

const deepFreeze = <T extends Record<string, unknown>>(obj: T): T => {
  for (const value of Object.values(obj)) {
    if (isPlainObject(value)) {
      deepFreeze(value);
    }
  }

  return Object.freeze(obj);
};

const getCacheForMonolingualize = (node: DesignerNodeData): WeakMap<MonolingualizeConfig, DesignerNode> => {
  const existing = designerNodeCache.get(node);
  if (existing) return existing;
  const cachedByLang = new WeakMap<MonolingualizeConfig, DesignerNode>();
  designerNodeCache.set(node, cachedByLang);
  return cachedByLang;
};

export const buildDesignerNodeCreate = ({
  getNodeTitle: getTitle,
  monolingualizeConfig,
  getNodeProfileById,
}: Options) => {
  const buildDesignerNode: BuildDesignerNode = (node) => {
    const profilingEnabled = profiler.isEnabled();
    const PROFILE_rootStart = profilingEnabled ? performance.now() : 0;
    const cacheForMonolingualize = getCacheForMonolingualize(node);
    const cached = cacheForMonolingualize.get(monolingualizeConfig);

    // If we have a cached node, check if the title has changed
    // eg module title depends on parentContext (InteractModule properties) which can change independently of nodeDesigns.
    if (cached) {
      const currentTitle = getTitle(cached);
      if (currentTitle === cached.title) {
        return cached;
      }
      // Title changed, rebuild and update cache below
    }

    const { id, typeId, config, childNodes, version, ...optionalProps } = node;
    const PROFILE_nodeStart = profilingEnabled ? performance.now() : 0;
    const PROFILE_configStart = profilingEnabled ? performance.now() : 0;
    const frozenConfig = deepFreeze(monolingualizeConfig(config)); // freezing to prevent accidental mutation by reference

    const PROFILE_configPhaseMs = profilingEnabled ? performance.now() - PROFILE_configStart : 0;

    const PROFILE_childrenStart = profilingEnabled ? performance.now() : 0;
    const builtChildNodes = Object.fromEntries(
      Object.entries(childNodes ?? {}).map(([name, childNodeOrNodes]) => {
        const builtChildNodeOrNodes = Array.isArray(childNodeOrNodes)
          ? childNodeOrNodes.map(buildDesignerNode)
          : childNodeOrNodes
            ? buildDesignerNode(childNodeOrNodes)
            : undefined;
        return [name, builtChildNodeOrNodes];
      }),
    );
    const PROFILE_childrenPhaseMs = profilingEnabled ? performance.now() - PROFILE_childrenStart : 0;

    const designerNode: Mutable<DesignerNode> = {
      id,
      typeId,
      config: frozenConfig,
      childNodes: builtChildNodes,
      version,
      ...optionalProps,
    };

    // The node's public API view (PublicDesignerNode.profile). Lazy so cost is only
    // paid on read; non-enumerable so spreads don't eagerly evaluate it.
    Object.defineProperty(designerNode, "profile", {
      get: () => getNodeProfileById(id),
      enumerable: false,
      configurable: true,
    });

    if (profilingEnabled) {
      const PROFILE_nodeDuration = performance.now() - PROFILE_nodeStart;
      profiler.recordBuild({
        id,
        typeId,
        nodeDuration: PROFILE_nodeDuration,
        configPhaseMs: PROFILE_configPhaseMs,
        childrenPhaseMs: PROFILE_childrenPhaseMs,
        childSlotNames: Object.keys(childNodes ?? {}),
      });
    }

    const title = getTitle(designerNode);

    if (title) {
      designerNode.title = title;
    }

    cacheForMonolingualize.set(monolingualizeConfig, designerNode);

    if (profilingEnabled) {
      profiler.setLastRootBuildTime(performance.now() - PROFILE_rootStart);
    }

    return designerNode;
  };
  return buildDesignerNode;
};
